import React, {FC, useState, useEffect} from "react"
import {withRouter, RouteComponentProps} from "react-router-dom"

import {getMessage, useIntl} from "@irati/utils/lang"
import magicLoginService from "@irati/auth/services/magicLogin"
import {getErrorMessage, getErrorValues} from "@irati/utils/lang/formatError"
import {CreateMagicLoginResponse, MagicLogin} from "@irati/types"
import magicLoginCodes from "@irati/auth/errorCodes/Magic"
import {setLoginProduct} from "@irati-tracking/tracker/trackers/mixpanel/platform"
import tracking from "@irati-tracking/platform/platform"
import {UserAccountForCookie, getUserAccountCookie} from "@irati/utils/cookies/userAccountCookie"
import getTenantFromPath from "@irati/identity/helpers/getTenantFromPath"
import History from "@irati/utils/history"

import LoginBackground from "../../components/LoginBackground"
import EnterEmail from "./EnterEmail"
import ConfirmCode from "./ConfirmCode"
import DomainSelector from "./DomainSelector"

enum Screens {
  enterEmail = "enterEmail",
  confirmCode = "confirmCode",
  domainSelector = "selectDomain"
}
interface Props extends RouteComponentProps {}

const OneTimePassword: FC<Props> = ({location}) => {
  const [screen, setScreen] = useState(Screens.enterEmail)
  const [identifier, setIdentifier] = useState("")
  const [magicLogin, setMagicLogin] = useState<CreateMagicLoginResponse>()
  const [load, setLoad] = useState<Promise<any> | null>(null)
  const [errorMsg, setErrorMsg] = useState("")
  const [multipleDomains, setMultipleDomains] = useState<MagicLogin[]>()
  const [tenantSSO, setTenantSSO] = useState<MagicLogin>()

  const {formatMessage} = useIntl()

  const handleActivation = (email: string) => {
    setErrorMsg("")
    if (email) {
      tracking.submitEmail()
      setIdentifier(email)
      sendOTPCode(email)
    }
  }

  const handleGoBack = () => {
    setIdentifier("")
    setScreen(Screens.enterEmail)
  }

  const sendOTPCode = (email: string) => {
    const loaded = magicLoginService
      .createMagicLogin(email)
      .then((res) => {
        setMagicLogin(res)
        setScreen(Screens.confirmCode)
      })
      .catch((err) => {
        setScreen(Screens.enterEmail)

        const isErrorUnknownEmail = err?.networkError?.errorCode === magicLoginCodes.MagicBadRequest && screen === Screens.enterEmail
        const message = isErrorUnknownEmail
          ? formatMessage({id: "errors.magic.PBMagicUnexpected"})
          : getMessage(formatMessage, getErrorMessage(err), getErrorValues(err))
        setErrorMsg(message)
      })
    setLoad(loaded)
  }

  const handleResendCode = () => {
    sendOTPCode(identifier)
  }

  const handleMultipleLogins = (logins: MagicLogin[]) => {
    setMultipleDomains(logins)
    setScreen(Screens.domainSelector)
  }

  const handleSSOLogin = (tenant: MagicLogin) => {
    setTenantSSO(tenant)
    setScreen(Screens.domainSelector)
  }

  useEffect(() => {
    const userAccounts: UserAccountForCookie[] = getUserAccountCookie()
    if (!getTenantFromPath() && userAccounts.length > 0){
      History.push("welcome/login/password")
    }
    if (location?.state?.noAccessTokenError) {
      setErrorMsg("platform.oneTimePassword.error.noAccessToken")
      History.replace({pathname: location.pathname, state: {}})
    }
    if (window.location.pathname.includes("welcome/login")) {
      setLoginProduct("otp_login")
    } else {
      setLoginProduct("activation")
    }
    tracking.startOTP()
  }, [])

  return (
    <LoginBackground>
      {screen === Screens.enterEmail && <EnterEmail load={load} onClick={handleActivation} error={errorMsg} />}
      {screen === Screens.confirmCode && (
        <ConfirmCode
          login={magicLogin}
          email={identifier}
          goBack={handleGoBack}
          onResend={handleResendCode}
          onMultipleLogins={handleMultipleLogins}
          onRedirect={handleSSOLogin}
        />
      )}
      {screen === Screens.domainSelector && <DomainSelector logins={multipleDomains} ssoTenant={tenantSSO} />}
    </LoginBackground>
  )
}

export default withRouter(OneTimePassword)
